/**
 * Prototype-only fixtures for the revenue import wizard: one vendor list and
 * two catalog previews — a clean one and one carrying the row-level errors and
 * conflicts the review step is built to flag.
 */
import type { CatalogPreview, ImportSummary, SupportedVendor } from "@/types";

export const VENDORS: SupportedVendor[] = [
  {
    id: "spotify",
    name: "Spotify",
    default_currency: "USD",
    supported_import_types: ["statement"],
  },
  {
    id: "label_worx",
    name: "Label Worx",
    default_currency: "EUR",
    supported_import_types: ["statement"],
  },
  {
    id: "believe",
    name: "Believe",
    default_currency: "EUR",
    supported_import_types: ["statement", "catalog"],
  },
];

export const IMPORT_SUMMARY: ImportSummary = {
  revenue_lines: 1284,
  releases_created: 2,
  recordings_created: 6,
  recordings_updated: 3,
  releases_updated: 1,
};

const EMPTY: CatalogPreview = {
  can_import: true,
  can_auto_import: false,
  releases_to_create: [],
  releases_to_update: [],
  likely_releases: [],
  conflicts_releases: [],
  recordings_to_create: [],
  recordings_to_update: [],
  likely_recordings: [],
  conflicts_recordings: [],
  errors: { release: [], recordings: [] },
  summary: {
    new_releases: 0,
    updated_releases: 0,
    new_recordings: 0,
    updated_recordings: 0,
    likely_recordings: 0,
    likely_releases: 0,
    conflicts_recordings: 0,
    conflicts_releases: 0,
    errors: 0,
  },
};

/** Nothing wrong: new items only, Save & Import goes straight through. */
export const CLEAN_PREVIEW: CatalogPreview = {
  ...EMPTY,
  releases_to_create: [
    {
      row_index: 2,
      match_status: "new",
      import_values: {
        title: "Night Ferry",
        upc_code: "5060204802256",
        catalog_number: "TRK-0142",
        territory: "Worldwide",
        contributors: ["Halla Vey"],
      },
    },
  ],
  recordings_to_create: [
    {
      row_index: 3,
      match_status: "new",
      import_values: {
        title: "Night Ferry",
        isrc: "GBKQU2100142",
        release_title: "Night Ferry",
        contributors: ["Halla Vey"],
      },
    },
    {
      row_index: 4,
      match_status: "new",
      import_values: {
        title: "Harbour Lights",
        isrc: "GBKQU2100143",
        release_title: "Night Ferry",
        contributors: ["Halla Vey"],
      },
    },
  ],
  summary: { ...EMPTY.summary, new_releases: 1, new_recordings: 2 },
};

/**
 * The review step with everything it can flag: rows the parser could not use
 * (match_status "error", warnings only reachable from the badge tooltip), and
 * rows that matched something already in the catalogue with different values.
 */
export const ROW_ERRORS_PREVIEW: CatalogPreview = {
  ...EMPTY,
  errors: {
    release: [
      {
        id: "err-release-1",
        row_index: 11,
        match_status: "error",
        warnings: ["UPC code 50602048022 is not a valid UPC (11 digits, expected 12 or 13)."],
        import_values: {
          title: "Coastal Roads",
          upc_code: "50602048022",
          catalog_number: "TRK-0155",
          contributors: ["Marn Ottoline"],
        },
      },
    ],
    recordings: [
      {
        id: "err-recording-1",
        row_index: 12,
        match_status: "error",
        warnings: [
          "ISRC is missing and the track title matches more than one recording in the catalogue.",
          "Revenue on this row cannot be attributed without an ISRC.",
        ],
        import_values: {
          title: "Coastal Roads",
          isrc: null,
          release_title: "Coastal Roads",
          contributors: ["Marn Ottoline"],
        },
      },
      {
        id: "err-recording-2",
        row_index: 27,
        match_status: "error",
        warnings: ['Revenue amount "1.2O3" could not be read as a number.'],
        import_values: {
          title: "Slow Tide",
          isrc: "GBKQU2100188",
          release_title: "Coastal Roads",
          contributors: ["Marn Ottoline"],
        },
      },
    ],
  },
  conflicts_recordings: [
    {
      id: "conflict-recording-1",
      item_type: "recording",
      row_index: 19,
      match_status: "conflict",
      matched_object_id: "rec-88",
      import_values: {
        title: "Paper Anchor (Radio Edit)",
        isrc: "GBKQU2100177",
        contributors: ["Halla Vey", "Juno Fields"],
      },
      catalog_values: {
        id: "rec-88",
        title: "Paper Anchor",
        isrc: "GBKQU2100177",
        contributors: [{ id: "con-1", display_name: "Halla Vey", country: "GB" }],
      },
      conflicted_fields: ["title", "artists"],
      warnings: [],
      resolution: null,
    },
  ],
  conflicts_releases: [
    {
      id: "conflict-release-1",
      item_type: "release",
      row_index: 18,
      match_status: "conflict",
      matched_object_id: "rel-44",
      import_values: {
        title: "Paper Anchor",
        upc_code: "5060204802263",
        catalog_number: "TRK-0150",
        territory: "Worldwide",
      },
      catalog_values: {
        id: "rel-44",
        title: "Paper Anchor EP",
        upc_code: "5060204802263",
        catalog_number: "TRK-0150-A",
        release_date: "2024-11-08",
        territory: "Worldwide",
        contributors: [{ id: "con-1", display_name: "Halla Vey", country: "GB" }],
      },
      conflicted_fields: ["title", "catalog_number"],
      warnings: [],
      resolution: null,
    },
  ],
  recordings_to_create: [
    {
      row_index: 20,
      match_status: "new",
      import_values: {
        title: "Quiet Berth",
        isrc: "GBKQU2100190",
        release_title: "Coastal Roads",
        contributors: ["Marn Ottoline"],
      },
    },
  ],
  summary: {
    ...EMPTY.summary,
    new_recordings: 1,
    conflicts_recordings: 1,
    conflicts_releases: 1,
    errors: 3,
  },
};
